import dayjs from 'dayjs'
import { useRolodexEntries } from 'features/rolodex'
import {
  CoverPageData,
  MainInfo,
} from 'pages/reporting/reports/raut/rautReportData'
import { useMemo } from 'react'
import {
  GeckoInspection,
  GeckoJob,
  GeckoOrganization,
  GeckoSite,
  GeckoUnit,
  RolodexEntryType,
  SectionId,
} from 'types'

interface CantileverData {
  inspections?: readonly GeckoInspection[]
  unit?: GeckoUnit
  site?: GeckoSite
  organization?: GeckoOrganization
  selectedJob?: GeckoJob
}

/**
 * Transforms Cantilever data into report-compatible format
 *
 * @param cantileverData Raw data from Cantilever
 * @returns Formatted data compatible with report forms
 */
export const getCantileverPrePopulatedData = (
  cantileverData: CantileverData
): Record<SectionId, Record<string, unknown>> => {
  const { unit, site, organization, selectedJob } = cantileverData

  // Asset/customer information - shared between main_info and cover_page
  const assetInfo = {
    asset: unit?.data?.name ? `${unit.data.name}` : '',
    customerName: organization?.data?.name || '',
    location: site?.data?.name || '',
  }

  // Date information - only for cover_page (not in mainInfo schema)
  const dateInfo = {
    startDate: selectedJob?.data?.start_date
      ? dayjs(selectedJob.data.start_date)
      : undefined,
    endDate: selectedJob?.data?.end_date
      ? dayjs(selectedJob.data.end_date)
      : undefined,
  }

  return {
    main_info: {
      ...assetInfo,
      scanIndicationMode: true,
    },
    cover_page: {
      ...assetInfo,
      ...dateInfo,
    },
    summary_page: {},
    calibration_page: {},
  } as Record<SectionId, Record<string, unknown>>
}

interface UseCantileverReportDataResult {
  prePopulatedData: Partial<MainInfo & CoverPageData>
  hasCantileverData: boolean
}

/**
 * Hook to manage Cantilever/Rolodex data integration with report forms
 *
 * Provides:
 * - Pre-populated data from inspection and component data
 * - Flag indicating if data is available
 *
 * @param selectedInspection - The selected inspection object
 * @param selectedComponent - The selected component object
 * @returns {UseCantileverReportDataResult} Object containing pre-populated data and availability flag
 */
export const useCantileverReportData = (
  inspections: readonly GeckoInspection[] | undefined
): UseCantileverReportDataResult => {
  const firstInspection = useMemo(() => inspections?.at(0), [inspections])
  const { units, sites, organizations, jobs } = useRolodexEntries(
    firstInspection
      ? {
          ids: [firstInspection.id],
          links: [
            RolodexEntryType.UNIT,
            RolodexEntryType.SITE,
            RolodexEntryType.ORGANIZATION,
            RolodexEntryType.JOB,
          ],
        }
      : undefined
  )

  const cantileverData = {
    inspections,
    unit: units.at(0),
    site: sites.at(0),
    organization: organizations.at(0),
    selectedJob: jobs.at(0),
  }

  const prePopulatedData = getCantileverPrePopulatedData(cantileverData)

  // Check if we have any data available
  const hasCantileverData = !!inspections && inspections.length > 0

  return {
    prePopulatedData,
    hasCantileverData,
  }
}
